import { TeamConfig } from "@/global/hooks/useTeamConfig";
import { CartManagerState } from "@/global/store/data/purchaseSeats/cartManager";
import {
  BucketsType,
  EventPackage,
  EventType,
} from "@/global/store/data/types";
import { PacksList } from "@/types";
import { NextRouter } from "next/router";

export function findActiveEventFromRouter(
  router: NextRouter,
  events: EventType[]
) {
  const eventCode = router?.query?.eventCode;
  if (!eventCode) return null;
  return events.find((itm) => itm.eventCode === eventCode) || null;
}

export function findSpecialEvents(events: EventType[]) {
  return events.filter((itm) => itm?.minEventSelectionRequired > 0);
}

export function filterEventsBySelection(
  selectedEvents: EventType[],
  newEvents: EventType[]
) {
  return newEvents.filter((itm) => {
    let selectedEventCodes = selectedEvents.map((itm) => itm.eventCode);
    return selectedEventCodes.includes(itm.eventCode) ? true : false;
  });
}

export function filterEventsWithoutSelected<T extends EventType[]>(
  events: T,
  selected: T
) {
  return events.filter((itm: any) => {
    return !selected.find((sel: any) => {
      return sel.eventCode == itm.eventCode;
    });
  });
}

export function filterEventsNotInCart(
  events: EventType[],
  cartState: CartManagerState
) {
  return events.filter((itm: any) => {
    return !cartState?.cart?.some((sel: any) => {
      return sel.eventCode == itm.eventCode;
    });
  });
}

export function isSpecialEventSelected(selectedEvents: EventType[]) {
  return selectedEvents.some((itm) => itm.minEventSelectionRequired > 0);
}

export function findBiggestMinCountSpecialEvent(events: EventType[]) {
  if (events.length == 0) return null;
  return events.reduce((itmA, itmB) => {
    return itmA.minEventSelectionRequired > itmB.minEventSelectionRequired
      ? itmA
      : itmB;
  });
}

export function findSmallestMinCountSpecialEvent(events: EventType[]) {
  if (events.length == 0) return null;
  return events.reduce((itmA, itmB) => {
    return itmA.minEventSelectionRequired < itmB.minEventSelectionRequired
      ? itmA
      : itmB;
  });
}

export function calcMinMaxEventCountRequired(
  teamConfig: TeamConfig,
  selectedBucket?: PacksList["buckets"][0] | BucketsType | null,
  selectedPackage?: EventPackage | null
) {
  let min = teamConfig?.purchaseSeatConfigs?.minGameSelectionCount || 0;
  let max = teamConfig?.purchaseSeatConfigs?.maxGameSelectionCount || Infinity;

  if (teamConfig?.packs?.enabled || teamConfig?.buckets?.enabled) {
    min = selectedBucket?.min || 0;
    max = selectedBucket?.max || Infinity;
    if (teamConfig?.buckets?.prioritizeMinGameSelectionCount) {
      min = teamConfig?.purchaseSeatConfigs?.minGameSelectionCount || 0;
    }
  }

  if (teamConfig?.packageConfigs?.isPackageEnabled) {
    min = selectedPackage?.minEventSelectionCount || 0;
    max = Infinity;
    if (selectedPackage?.isBuildYourOwn) {
      min = teamConfig?.purchaseSeatConfigs?.minGameSelectionCount || 0;
    }
  }

  return {
    minEventCountRequired: min,
    maxEventCountRequired: max,
  };
}

export function isBucketMinMaxEventSelectionReached(
  bucket: PacksList["buckets"][0] | BucketsType | null,
  selectedEvents: EventType[],
  teamConfig: TeamConfig
) {
  let min = 0;
  let max = Infinity;

  if (bucket?.min) min = bucket.min;
  if (bucket?.max) max = bucket.max;

  if (teamConfig?.buckets?.prioritizeMinGameSelectionCount) {
    min = teamConfig?.purchaseSeatConfigs?.minGameSelectionCount || 0;
  }

  const selectedEventsByBucket = selectedEvents.filter((evt) => {
    return evt?.bucketId === bucket?.id;
  });

  const isMinEventCountReached = selectedEventsByBucket.length >= min;
  let isMaxEventCountReached =
    max == Infinity ? false : selectedEventsByBucket.length >= max;

  if (
    teamConfig?.buckets?.prioritizeMaxGameSelectionCount &&
    teamConfig?.purchaseSeatConfigs?.maxGameSelectionCount &&
    selectedEvents.length >=
      teamConfig?.purchaseSeatConfigs?.maxGameSelectionCount
  ) {
    isMaxEventCountReached = true;
  }

  return { isMinEventCountReached, isMaxEventCountReached };
}

export function isRegularEventsMinMaxSelectionReached(
  selectedEvents: EventType[],
  teamConfig: TeamConfig
) {
  let min = teamConfig?.purchaseSeatConfigs?.minGameSelectionCount || 0;
  let max = teamConfig?.purchaseSeatConfigs?.maxGameSelectionCount || Infinity;

  const isMinEventCountReached = selectedEvents.length >= min;
  const isMaxEventCountReached =
    max == Infinity ? false : selectedEvents.length >= max;

  return { isMinEventCountReached, isMaxEventCountReached };
}

export function findBucketByEventCode(
  selectedPack: PacksList | undefined,
  buckets: BucketsType[],
  teamConfig: TeamConfig,
  eventCode: string
) {
  let bucket = null;

  if (teamConfig?.buckets?.enabled) {
    bucket =
      buckets.find((bkt) => {
        const bktEventCodes = bkt?.events?.map((itm) => itm.eventCode) || [];
        return bktEventCodes.includes(eventCode);
      }) || null;
  } else if (teamConfig?.packs?.enabled) {
    bucket =
      selectedPack?.buckets?.find((bkt) => {
        const bktEventCodes = bkt?.events?.map((itm) => itm.eventCode) || [];
        return bktEventCodes.includes(eventCode);
      }) || null;
  }

  return bucket;
}

// function to get missing bucket events
export function findMissingBucketEvents(
  bucket: PacksList["buckets"][0] | BucketsType | null,
  selectedEvents: EventType[],
  eventCodeToFindBucket?: string
) {
  if (!eventCodeToFindBucket || !bucket) return [];

  return (
    bucket?.events?.filter((evt) => {
      const eventCodes = selectedEvents.map((evt) => evt.eventCode);
      return (
        !eventCodes.includes(evt.eventCode) &&
        evt.eventCode !== eventCodeToFindBucket
      );
    }) || []
  );
}
